<template>
  <span
    ref="trigger"
    class="pointer-events-auto inline-flex"
    @mouseenter="onHoverOrFocus"
    @mouseleave="onLeave"
    @focusin="onHoverOrFocus"
    @focusout="onLeave"
    @click="togglePinned"
    @keydown.esc="dismiss"
  >
    <slot :tooltipId="tooltipId" />
    <Teleport to="body">
      <div
        v-if="tooltipVisible"
        :id="tooltipId"
        ref="tooltipEl"
        class="hoverable-tooltip__content popover-panel pointer-events-none fixed z-(--z-popover)
               w-max max-w-58 px-2 py-1.5 text-center text-label leading-tight text-text-soft"
        :class="props.contentClass"
        role="tooltip"
        :style="tooltipStyle"
      >
        <slot name="tooltip">
          {{ props.text }}
        </slot>
      </div>
    </Teleport>
  </span>
</template>

<script setup>
import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  useId,
} from 'vue';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: String,
    default: '',
  },
  // a click pins the tooltip open until the next click; on touch screens a tap is the only way to show it
  toggleOnClick: {
    type: Boolean,
    default: false,
  },
});

const tooltipId = useId();
// touch screens fire mouseenter and focusin on every tap, which would undo the tap toggle
const canHover = window.matchMedia('(hover: hover)').matches;
const pinned = ref(false);

const trigger = ref(null);
const tooltipEl = ref(null);
const tooltipVisible = ref(false);
const tooltipReady = ref(false);
const tooltipPosition = ref({
  left: 0,
  top: 0,
});

let listenersAttached = false;

const tooltipStyle = computed(() => ({
  left: `${tooltipPosition.value.left}px`,
  top: `${tooltipPosition.value.top}px`,
  visibility: tooltipReady.value ? 'visible' : 'hidden',
}));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const updateTooltipPosition = () => {
  if (!trigger.value || !tooltipEl.value) {
    return;
  }

  const gap = 8;
  const viewportPadding = 8;
  const triggerRect = trigger.value.getBoundingClientRect();
  const tooltipRect = tooltipEl.value.getBoundingClientRect();
  const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
  const maxTop = window.innerHeight - tooltipRect.height - viewportPadding;

  const centeredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  const left = clamp(centeredLeft, viewportPadding, Math.max(viewportPadding, maxLeft));

  let top = triggerRect.top - tooltipRect.height - gap;
  if (top < viewportPadding) {
    top = triggerRect.bottom + gap;
  }

  tooltipPosition.value = {
    left,
    top: clamp(top, viewportPadding, Math.max(viewportPadding, maxTop)),
  };
};

const addPositionListeners = () => {
  if (listenersAttached) {
    return;
  }
  window.addEventListener('resize', updateTooltipPosition);
  window.addEventListener('scroll', updateTooltipPosition, true);
  listenersAttached = true;
};

const removePositionListeners = () => {
  if (!listenersAttached) {
    return;
  }
  window.removeEventListener('resize', updateTooltipPosition);
  window.removeEventListener('scroll', updateTooltipPosition, true);
  listenersAttached = false;
};

const showTooltip = async () => {
  if (props.disabled) {
    return;
  }
  tooltipVisible.value = true;
  tooltipReady.value = false;
  await nextTick();
  if (!tooltipVisible.value) {
    return;
  }
  updateTooltipPosition();
  tooltipReady.value = true;
  addPositionListeners();
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipReady.value = false;
  removePositionListeners();
};

const onHoverOrFocus = () => {
  if (props.toggleOnClick && !canHover) {
    return;
  }
  showTooltip();
};

const onLeave = () => {
  if (pinned.value) {
    return;
  }
  hideTooltip();
};

const togglePinned = () => {
  if (!props.toggleOnClick) {
    return;
  }
  pinned.value = !pinned.value;
  if (pinned.value) {
    showTooltip();
  } else {
    hideTooltip();
  }
};

const unpin = () => {
  pinned.value = false;
  hideTooltip();
};

// Esc hides the tooltip without moving the pointer or focus, and page-level Esc handlers don't see it
const dismiss = (event) => {
  if (!tooltipVisible.value) {
    return;
  }
  event.stopPropagation();
  unpin();
};

onClickOutside(trigger, () => {
  if (pinned.value) {
    unpin();
  }
});

onUnmounted(() => {
  removePositionListeners();
});
</script>
